import React, { useState } from "react";
import { supabase } from "../../../lib/supabaseClient";

type Props = {
  googleName: string;
  userId: string;
  avatarUrl?: string;
  onComplete: () => void;
};

const UsernameModal: React.FC<Props> = ({ googleName, userId, avatarUrl, onComplete }) => {
  const [displayName, setDisplayName] = useState(googleName);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate username suggestion from display name
  const generateUsername = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]/g, "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!displayName.trim()) {
      setError("Display name cannot be empty");
      return;
    }

    if (displayName.trim().length < 2) {
      setError("Display name must be at least 2 characters");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const username = generateUsername(displayName.trim());

      // Check if username already exists
      const { data: existingUser } = await supabase.from("user_profiles").select("username").eq("username", username).maybeSingle();

      let finalUsername = username;

      // If username exists, add random number
      if (existingUser) {
        finalUsername = `${username}${Math.floor(Math.random() * 9000) + 1000}`;
      }

      // Create user profile
      const { error: insertError } = await supabase.from("user_profiles").insert([
        {
          id: userId,
          google_name: googleName,
          username: finalUsername,
          display_name: displayName.trim(),
          avatar_url: avatarUrl || null,
        },
      ]);

      if (insertError) {
        throw insertError;
      }

      onComplete();
    } catch (err: any) {
      // console.error("Profile creation error:", err);
      setError(err.message || "Failed to create profile");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border-2 border-red-600 rounded-xl max-w-md w-full p-6 shadow-[0_0_40px_rgba(220,38,38,0.5)]">
        <h2 className="text-2xl font-bold text-white mb-4">Welcome to Noleyo! 🎬</h2>
        <p className="text-gray-400 mb-6">Choose your display name to get started</p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-300 mb-2">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
              className="w-full px-4 py-3 rounded-lg bg-gray-800 border-2 border-gray-700 focus:border-red-600 focus:outline-none text-white"
              disabled={loading}
              maxLength={50}
            />
            <p className="text-xs text-gray-500 mt-2">
              Username will be: <span className="text-cyan-400">{generateUsername(displayName || "user")}</span>
            </p>
          </div>

          {error && <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">{error}</div>}

          <button
            type="submit"
            disabled={loading || !displayName.trim()}
            className="w-full py-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            {loading ? "Creating Profile..." : "Continue"}
          </button>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">You can change your display name later in your profile settings</p>
      </div>
    </div>
  );
};

export default UsernameModal;
